import { damageReportsApi, documentsApi, dossiersApi, insurancesApi, maintenanceActivitiesApi, propertiesApi, remindersApi, rentalAgreementsApi, type DamageReportDto, type DocumentDto, type InsuranceDto, type MaintenanceActivityDto, type PropertyDto, type ReminderDto, type RentalAgreementDto } from '../../services/api';
import { onLocaleChange, t } from '../../services/i18n';
import { completePropertyReminder, deletePropertyDocument, deletePropertyReminder, deletePropertyMaintenanceActivity, openDocumentDialog, openEditAgreementDialog, openEditPropertyDialog, openLinkTenantDialog, openMaintenanceActivityDialog, openNewTenantDialog, openPropertyDossierExportDialog, openReminderDialog, openRentChangeDialog } from './property-details-dialogs';
import { openInsuranceCreateDialog, openInsuranceEditDialog } from '../insurance/insurance-dialogs';
import './rc-property-details-view';
import { RcPropertyDetailsView } from './rc-property-details-view';
import type { DetailTab } from './property-shared';

type PropertyDetailsEventDetail = {
  id?: string;
  tab?: string;
};

const DETAIL_EVENTS = ['edit-property', 'export-dossier', 'add-tenant', 'link-tenant', 'add-doc', 'add-reminder', 'add-maintenance-activity', 'edit-agreement', 'rent-change', 'download-doc', 'delete-doc', 'complete-reminder', 'delete-reminder', 'download-maintenance-attachment', 'delete-maintenance-activity', 'open-tab', 'open-details', 'add-insurance', 'edit-insurance', 'delete-insurance'] as const;

export class RcPropertyDetails extends HTMLElement {
  private propertyId = '';
  private property: PropertyDto | null = null;
  private agreements: RentalAgreementDto[] = [];
  private documents: DocumentDto[] = [];
  private reminders: ReminderDto[] = [];
  private maintenanceActivities: MaintenanceActivityDto[] = [];
  private insurances: InsuranceDto[] = [];
  private damageReports: DamageReportDto[] = [];
  private activeTab: DetailTab = 'tenants';
  private localeCleanup: (() => void) | null = null;

  connectedCallback(): void {
    this.propertyId = this.getAttribute('property-id') || '';
    const initialTab = this.getAttribute('initial-tab');
    if (initialTab) this.activeTab = initialTab as DetailTab;
    this.localeCleanup?.();
    this.localeCleanup = onLocaleChange(() => this.render());
    DETAIL_EVENTS.forEach((eventName) => this.addEventListener(`property-details-${eventName}`, this.handleDetailEvent as EventListener));
    this.addEventListener('property-tab-change', this.handleTabChange as EventListener);
    this.addEventListener('property-gallery-changed', this.handleGalleryChanged as EventListener);
    this.addEventListener('property-contract-renewals-changed', this.handleContractRenewalsChanged as EventListener);
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    if (this.propertyId) {
      const autoAction = this.getAttribute('auto-action') || '';
      this.loadAll().then(() => {
        if (autoAction === 'add-tenant') openNewTenantDialog(this.propertyId, async () => this.loadAll());
        else if (autoAction === 'add-doc') openDocumentDialog(this.propertyId, async () => this.loadAll());
        else if (autoAction === 'add-reminder') openReminderDialog(this.propertyId, async () => this.loadAll());
      });
    }
  }

  disconnectedCallback(): void {
    this.localeCleanup?.();
    this.localeCleanup = null;
    DETAIL_EVENTS.forEach((eventName) => this.removeEventListener(`property-details-${eventName}`, this.handleDetailEvent as EventListener));
    this.removeEventListener('property-tab-change', this.handleTabChange as EventListener);
    this.removeEventListener('property-gallery-changed', this.handleGalleryChanged as EventListener);
    this.removeEventListener('property-contract-renewals-changed', this.handleContractRenewalsChanged as EventListener);
  }

  async loadAll(): Promise<void> {
    try {
      [this.property, this.agreements, this.documents, this.reminders, this.maintenanceActivities, this.insurances, this.damageReports] = await Promise.all([
        propertiesApi.getById(this.propertyId),
        rentalAgreementsApi.getByProperty(this.propertyId),
        documentsApi.getByProperty(this.propertyId),
        remindersApi.getByProperty(this.propertyId),
        maintenanceActivitiesApi.listByProperty(this.propertyId),
        insurancesApi.getByProperty(this.propertyId),
        damageReportsApi.listByProperty(this.propertyId),
      ]);
      this.render();
      this.dispatchEvent(new CustomEvent('property-details-loaded', {
        bubbles: true,
        detail: { property: this.property },
      }));
    } catch (error) {
      console.error('Failed to load property details:', error);
      this.innerHTML = `<div class="empty-state">${t('property.details.loadError')}</div>`;
    }
  }

  private render(): void {
    if (!this.property) return;
    this.innerHTML = '<rc-property-details-view></rc-property-details-view>';
    const view = this.querySelector('rc-property-details-view') as RcPropertyDetailsView | null;
    if (!view) return;
    view.property = this.property;
    view.agreements = this.agreements;
    view.documents = this.documents;
    view.reminders = this.reminders;
    view.maintenanceActivities = this.maintenanceActivities;
    view.insurances = this.insurances;
    view.damageReports = this.damageReports;
    view.activeTab = this.activeTab;
    view.render();
  }

  private handleTabChange = (event: CustomEvent<{ tab: DetailTab }>): void => {
    if (!event.detail?.tab) return;
    this.activeTab = event.detail.tab;
  };

  private handleGalleryChanged = async (): Promise<void> => {
    await this.loadAll();
  };

  private handleContractRenewalsChanged = async (): Promise<void> => {
    await this.loadAll();
  };

  private handleDetailEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<PropertyDetailsEventDetail>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'property-details-edit-property':
        if (this.property) openEditPropertyDialog(this.property, async () => this.loadAll());
        return;
      case 'property-details-export-dossier':
        openPropertyDossierExportDialog(this.propertyId, (blob, fileName) => this.downloadBlob(blob, fileName));
        return;
      case 'property-details-add-tenant':
        openNewTenantDialog(this.propertyId, async () => this.loadAll());
        return;
      case 'property-details-link-tenant':
        await openLinkTenantDialog(this.propertyId, this.agreements, async () => this.loadAll());
        return;
      case 'property-details-add-doc':
        openDocumentDialog(this.propertyId, async () => this.loadAll());
        return;
      case 'property-details-add-reminder':
        openReminderDialog(this.propertyId, async () => this.loadAll());
        return;
      case 'property-details-add-maintenance-activity':
        openMaintenanceActivityDialog(this.propertyId, async () => this.loadAll());
        return;
      case 'property-details-edit-agreement': {
        const agreement = this.agreements.find((item) => item.id === detail.id);
        if (agreement) openEditAgreementDialog(agreement, async () => this.loadAll());
        return;
      }
      case 'property-details-rent-change': {
        const agreement = this.agreements.find((item) => item.id === detail.id);
        if (agreement) openRentChangeDialog(agreement, async () => this.loadAll());
        return;
      }
      case 'property-details-download-doc':
        if (detail.id) await this.handleDocDownload(detail.id);
        return;
      case 'property-details-delete-doc':
        if (detail.id) await this.handleDocDelete(detail.id);
        return;
      case 'property-details-complete-reminder':
        if (detail.id) await this.handleReminderComplete(detail.id);
        return;
      case 'property-details-delete-reminder':
        if (detail.id) await this.handleReminderDelete(detail.id);
        return;
      case 'property-details-download-maintenance-attachment':
        if (detail.id) await this.handleMaintenanceAttachmentDownload(detail.id);
        return;
      case 'property-details-delete-maintenance-activity':
        if (detail.id) await this.handleMaintenanceActivityDelete(detail.id);
        return;
      case 'property-details-add-insurance':
        openInsuranceCreateDialog(this.propertyId, async () => this.loadAll());
        return;
      case 'property-details-edit-insurance': {
        const insurance = this.insurances.find((item) => item.id === detail.id);
        if (insurance) openInsuranceEditDialog(insurance, async () => this.loadAll());
        return;
      }
      case 'property-details-delete-insurance':
        if (detail.id) await this.handleInsuranceDelete(detail.id);
        return;
      case 'property-details-open-tab':
        if (detail.tab) {
          this.activeTab = detail.tab as DetailTab;
          this.render();
        }
        return;
      case 'property-details-open-details':
        if (detail.id) {
          window.location.assign(`/property/?property=${encodeURIComponent(detail.id)}&from=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        }
        return;
      default:
        return;
    }
  };

  private async handleDocDownload(id: string): Promise<void> {
    try {
      const document = this.documents.find((item) => item.id === id);
      await documentsApi.download(id, document?.fileName);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  }

  private async handleDocDelete(id: string): Promise<void> {
    try {
      const deleted = await deletePropertyDocument(id);
      if (deleted) await this.loadAll();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  }

  private async handleReminderComplete(id: string): Promise<void> {
    const reminder = this.reminders.find((item) => item.id === id);
    if (!reminder) return;
    try {
      await completePropertyReminder(reminder);
      await this.loadAll();
    } catch (error) {
      console.error('Failed to complete reminder:', error);
    }
  }

  private async handleReminderDelete(id: string): Promise<void> {
    try {
      const deleted = await deletePropertyReminder(id);
      if (deleted) await this.loadAll();
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  }

  private async handleInsuranceDelete(id: string): Promise<void> {
    try {
      const { confirmDialog } = await import('../shared/rc-confirm-dialog');
      if (!await confirmDialog(t('insurance.confirmDelete'))) return;
      await insurancesApi.delete(id);
      await this.loadAll();
    } catch (error) {
      console.error('Failed to delete insurance:', error);
    }
  }

  private async handleMaintenanceAttachmentDownload(id: string): Promise<void> {
    try {
      const activity = this.maintenanceActivities.find((item) => item.id === id);
      await maintenanceActivitiesApi.downloadAttachment(id, activity?.attachmentFileName);
    } catch (error) {
      console.error('Failed to download maintenance attachment:', error);
    }
  }

  private async handleMaintenanceActivityDelete(id: string): Promise<void> {
    try {
      const deleted = await deletePropertyMaintenanceActivity(id);
      if (deleted) await this.loadAll();
    } catch (error) {
      console.error('Failed to delete maintenance activity:', error);
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

if (!customElements.get('rc-property-details')) {
  customElements.define('rc-property-details', RcPropertyDetails);
}
